import { useMemo, useState } from "react";
import { useNavigation } from "../app/navigation";
import { useWorkspace } from "../app/workspace";
import type { AttendanceStatus } from "../domain/types";
import { AppShell } from "../components/AppShell";
import { WorkspaceGate } from "../components/WorkspaceGate";

const statusLabels: Array<{ value: AttendanceStatus; label: string }> = [
  { value: "present", label: "출석" },
  { value: "late", label: "지각" },
  { value: "absent", label: "결석" },
  { value: "left_early", label: "조퇴" },
];

export function ClassSheetPage({ classId }: { classId?: string | undefined }) {
  return (
    <WorkspaceGate roles={["owner", "manager", "instructor", "desk", "sample_user"]} startSampleOnEntry={!classId}>
      <ClassSheet classId={classId} />
    </WorkspaceGate>
  );
}

function ClassSheet({ classId }: { classId?: string | undefined }) {
  const {
    state,
    command,
    mutating,
    offlineConflicts,
    resolveOfflineConflict,
    role,
    mode,
  } = useWorkspace();
  const canWriteLearning = role !== "desk";
  const { navigate } = useNavigation();
  const [savingStudentId, setSavingStudentId] = useState<string | null>(null);
  const [showLearning, setShowLearning] = useState(false);
  const session =
    state!.sessions.find((item) =>
      classId
        ? item.classId === classId && item.state === "in_progress"
        : item.id === "session-math-today",
    ) ??
    state!.sessions.find((item) =>
      classId ? item.classId === classId : item.state === "in_progress",
    ) ??
    state!.sessions[0];
  const classGroup = state!.classes.find(
    (item) => item.id === session?.classId,
  );
  const records = useMemo(
    () => state!.attendance.filter((item) => item.sessionId === session?.id),
    [session?.id, state],
  );
  const marked = records.filter((item) => item.status !== "unmarked").length;

  async function mark(studentId: string, status: AttendanceStatus) {
    if (!session) return;
    setSavingStudentId(studentId);
    try {
      await command({
        type: "attendance.mark",
        sessionId: session.id,
        studentId,
        status,
      });
      if (status === "absent") navigate("/close/today");
    } finally {
      setSavingStudentId(null);
    }
  }

  if (!session || !classGroup)
    return (
      <AppShell title="지금 수업">
        <section className="empty-state">
          <h2>예정된 수업이 없어요</h2>
          <p>수업을 만들면 시간순 명단이 여기에 나타납니다.</p>
        </section>
      </AppShell>
    );

  return (
    <AppShell
      title="지금 수업"
      description={`${classGroup.name} · ${classGroup.startTime}–${classGroup.endTime}`}
      actions={
        canWriteLearning ? <button
          type="button"
          className="secondary-action"
          onClick={() => setShowLearning(true)}
        >
          진도·과제 기록
        </button> : null
      }
    >
      <section className="class-sheet" aria-labelledby="class-sheet-title">
        <header className="class-sheet__summary">
          <div>
            <p>현재 명단</p>
            <h2 id="class-sheet-title">{classGroup.name}</h2>
            <span>
              {classGroup.instructorName} · {classGroup.room}
            </span>
          </div>
          <div
            className="attendance-progress"
            aria-label={`${records.length}명 중 ${marked}명 기록`}
          >
            <strong>{marked}</strong>
            <span>/ {records.length}명</span>
            <i
              style={
                {
                  "--progress": `${records.length ? (marked / records.length) * 100 : 0}%`,
                } as React.CSSProperties
              }
            />
          </div>
          <button
            type="button"
            className="bulk-action"
            disabled={mutating || marked === records.length}
            onClick={() =>
              void command({
                type: "attendance.bulkPresent",
                sessionId: session.id,
              })
            }
          >
            남은 {records.length - marked}명 모두 출석
          </button>
        </header>
        {mode === "sample" ? (
          <p className="sheet-guide">
            <strong>둘러보기 과제</strong> 김민서 학생을 결석으로 기록해 보세요.
            수강 횟수·보강·안내가 같은 출결 기록에 연결됩니다.
          </p>
        ) : null}
        <div
          className="attendance-list"
          role="list"
          aria-label="학생 출결 명단"
        >
          {records.map((record) => {
            const student = state!.students.find(
              (item) => item.id === record.studentId,
            );
            const conflict = offlineConflicts.find(
              (item) =>
                item.command.sessionId === record.sessionId &&
                item.command.studentId === record.studentId,
            );
            if (!student) return null;
            return (
              <article
                key={record.id}
                className={`attendance-row ${mode === "sample" && student.id === "student-01" ? "attendance-row--guided" : ""} ${conflict ? "attendance-row--conflict" : ""}`}
                role="listitem"
                aria-busy={savingStudentId === student.id}
              >
                <div className="student-identity">
                  <span aria-hidden="true">
                    {student.displayName.slice(-2)}
                  </span>
                  <div>
                    <strong>{student.displayName}</strong>
                    <small>
                      {student.schoolGrade} ·{" "}
                      {student.billingPlan === "session"
                        ? `남은 ${student.remainingSessions}회`
                        : "월정액"}
                    </small>
                  </div>
                </div>
                <div
                  className="attendance-choices"
                  role="group"
                  aria-label={`${student.displayName} 출결 선택`}
                >
                  {statusLabels.map((status) => (
                    <button
                      key={status.value}
                      type="button"
                      className={
                        record.status === status.value ? "is-selected" : ""
                      }
                      aria-pressed={record.status === status.value}
                      disabled={mutating}
                      onClick={() => void mark(student.id, status.value)}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
                <span className={`sync-label sync-label--${record.syncState}`}>
                  {record.syncState === "synced"
                    ? "저장됨"
                    : record.syncState === "pending"
                      ? "전송 대기"
                      : record.syncState === "conflict"
                        ? "충돌 확인"
                        : "기록 전"}
                </span>
                {conflict ? (
                  <div className="conflict-resolution" role="alert">
                    <div>
                      <strong>다른 기기 값과 달라요</strong>
                      <span>
                        서버 {attendanceText(conflict.serverValue)} · 이 기기{" "}
                        {attendanceText(conflict.localValue)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="secondary-action"
                      onClick={() =>
                        void resolveOfflineConflict(conflict.id, "server")
                      }
                    >
                      서버 값 유지
                    </button>
                    <button
                      type="button"
                      className="primary-action"
                      onClick={() =>
                        void resolveOfflineConflict(conflict.id, "local")
                      }
                    >
                      이 기기 값 저장
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </section>
      {showLearning ? (
        <LearningDialog
          sessionId={session.id}
          onClose={() => setShowLearning(false)}
        />
      ) : null}
    </AppShell>
  );
}

function attendanceText(value: string) {
  return (
    {
      unmarked: "기록 전",
      present: "출석",
      absent: "결석",
      late: "지각",
      left_early: "조퇴",
    }[value] ?? value
  );
}

function LearningDialog({
  sessionId,
  onClose,
}: {
  sessionId: string;
  onClose: () => void;
}) {
  const { command, mutating } = useWorkspace();
  const [progress, setProgress] = useState("일차방정식 활용 문제 3–6번");
  const [homework, setHomework] = useState("오답 3문제 풀이 과정 다시 쓰기");
  const [visibility, setVisibility] = useState<"internal" | "guardian">(
    "internal",
  );
  async function save() {
    await command({
      type: "learning.save",
      sessionId,
      progress,
      homework,
      visibility,
    });
    onClose();
  }
  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="learning-title"
      >
        <header>
          <div>
            <p>중1 수학 A</p>
            <h2 id="learning-title">진도·과제 기록</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </header>
        <label>
          오늘 진도
          <textarea
            value={progress}
            onChange={(event) => setProgress(event.target.value)}
          />
        </label>
        <label>
          과제
          <textarea
            value={homework}
            onChange={(event) => setHomework(event.target.value)}
          />
        </label>
        <fieldset>
          <legend>누가 볼 수 있나요?</legend>
          <label>
            <input
              type="radio"
              name="visibility"
              checked={visibility === "internal"}
              onChange={() => setVisibility("internal")}
            />{" "}
            강사와 원장만
          </label>
          <label>
            <input
              type="radio"
              name="visibility"
              checked={visibility === "guardian"}
              onChange={() => setVisibility("guardian")}
            />{" "}
            보호자에게도 공개
          </label>
        </fieldset>
        <p className="form-hint">
          학생별 민감 메모는 기본으로 보호자에게 보이지 않습니다.
        </p>
        <footer>
          <button type="button" className="secondary-action" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="primary-action"
            onClick={() => void save()}
            disabled={mutating || (!progress.trim() && !homework.trim())}
          >
            {mutating ? "저장 중…" : "기록 저장"}
          </button>
        </footer>
      </section>
    </div>
  );
}
